import { Injectable } from '@angular/core';
import { InstanceService } from '@commonspirit/ngx-instance';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { Location, LocationHour } from './../../../shared/models/v2/search';
import { Meta } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class TagHandlerService {
  weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  constructor(private instanceService: InstanceService, private metaService: Meta) {}

  get LinkCanonicals(): boolean {
    return this.instanceService.getConfiguration(ConfigurationKey.LinkCanonicals)?.value as boolean;
  }

  setPageCanonicals(url: string): void {
    if (this.LinkCanonicals) {
      this.setMetaLink('canonical', url);
      this.setMetaProperty('og:url', url);
    }
  }

  setNoFollowMetaPage(): void {
    this.setMetaName('robots', 'noindex,nofollow');
  }

  removePageCanonicals(strUrl: string): void {
    try {
      if (this.LinkCanonicals) {
        const element = document.querySelectorAll(`link[rel='canonical']`) || null;
        if (element != null) {
          element.forEach((x) => {
            if (x.getAttribute('href') === strUrl) {
              x.remove();
            }
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  setLocationDetailsMeta(location: Location, locationTitle: string, imgUrl: string, services: string[]): void {
    if (this.LinkCanonicals) {
      const marketCode = this.instanceService.getConfiguration(ConfigurationKey.MarketCodes)?.value as string;
      const shortcutIconUrl = (this.instanceService.getConfiguration(ConfigurationKey.ShortcutIcon)?.value as string)
        ? window.location.host + this.instanceService.getConfiguration(ConfigurationKey.ShortcutIcon)?.value
        : '';
      const discription = location?.aboutLocation?.split('</p>')[0]?.replace(/<[^>]*>/g, '');
      this.setMetaName('keywords', services.join(','));
      this.setMetaName('viewport', 'width=device-width, initial-scale=1');
      this.setMetaName('description', discription);
      this.setMetaProperty('og:title', locationTitle);
      this.setMetaProperty('twitter:title', locationTitle);
      this.setMetaProperty('og:description', discription);
      this.setMetaProperty('twitter:description', discription);
      this.setMetaProperty('og:type', 'WEBSITE');
      this.setMetaProperty('twitter:card', 'summary_large_image');
      this.setMetaProperty('og:site_name', marketCode);
      this.setMetaProperty('twitter:site', marketCode);
      this.setMetaProperty('og:image', imgUrl);
      this.setMetaProperty('twitter:image', imgUrl);
      this.setMetaLink('shortcut icon', shortcutIconUrl);
    }
  }

  setMetaName(metaName: string, metaContent: string): void {
    const metaDescription = document.querySelector(`meta[name='${metaName}']`) || null;
    if (metaDescription === null) {
      this.metaService.addTag({ name: metaName, content: metaContent });
    } else {
      this.metaService.updateTag({ name: metaName, content: metaContent });
    }
  }

  setMetaProperty(metaProperty: string, metaContent: string): void {
    const metaDescription = document.querySelector(`meta[property='${metaProperty}']`) || null;
    if (metaDescription === null) {
      this.metaService.addTag({ property: metaProperty, content: metaContent });
    } else {
      this.metaService.updateTag({ property: metaProperty, content: metaContent });
    }
  }

  removeMetaNoIndexProperty(): void {
    this.metaService.removeTag("name='robots'");
  }

  setMetaLink(linkRel: string, linkUrl: string): void {
    const head = document.getElementsByTagName('head')[0];
    let element = (document.querySelector(`link[rel='${linkRel}']`) as HTMLLinkElement) || null;
    if (element === null) {
      element = document.createElement('link');
      head.appendChild(element);
    }
    element.setAttribute('rel', linkRel);
    element.setAttribute('href', linkUrl);
  }

  setResultPageMeta(locationTitle: string, description: string): void {
    if (this.LinkCanonicals) {
      const marketCode = this.instanceService.getConfiguration(ConfigurationKey.MarketCodes)?.value as string;
      const shortcutIconUrl = (this.instanceService.getConfiguration(ConfigurationKey.ShortcutIcon)?.value as string)
        ? window.location.host + this.instanceService.getConfiguration(ConfigurationKey.ShortcutIcon)?.value
        : '';
      this.setMetaName('viewport', 'width=device-width, initial-scale=1');
      this.setMetaProperty('og:title', locationTitle);
      this.setMetaProperty('twitter:title', locationTitle);
      this.setMetaProperty('og:description', description);
      this.setMetaProperty('twitter:description', description);
      this.setMetaProperty('og:type', 'WEBSITE');
      this.setMetaProperty('og:site_name', marketCode);
      this.setMetaProperty('twitter:site', marketCode);
      this.setMetaLink('shortcut icon', shortcutIconUrl);
    }
  }

  setLocationSchema(location: Location): void {
    if (!this.LinkCanonicals || !location || !location.type) {
      return;
    }
    this.removeschema();
    const body = document.getElementsByTagName('head')[0];
    const script = document.createElement('script');
    script.type = `application/ld+json`;
    script.text = this.createJsonLd(location);
    body.appendChild(script);
  }

  removeschema(): void {
    const element = (document.querySelector(`script[type='application/ld+json']`) as HTMLLinkElement) || null;
    if (element) {
      element.remove();
    }
  }

  createJsonLd(location: Location): string {
    const imageUrl = location.headerImage?.[0].imageUrl?.[0] ?? '';
    const schemaType = location.type.toLocaleLowerCase() == 'emergency services' ? 'Hospital' : 'MedicalClinic';
    const specialities = location.services?.map((service) => `"${service.specialty}"`)?.join(',');
    return `{
        "@context": "https://schema.org",
        "@type": ["${schemaType}"],
        "name": "${location.name ?? ''}", 
        "url": "${window.location.href ?? ''}",
        "image": "${imageUrl}",
        "description": "${
          location.aboutLocation
            ? location.aboutLocation
                .split('.', 2)
                .join('. ')
                .replace('&nbsp;', '')
                .replace(/<\/?[^>]+(>|$)/g, '')
            : ''
        }",
        ${
          location.address
            ? `"address": {
          "@type":"PostalAddress",
            "streetAddress":"${location.address.addressLine1 ?? ''}${
                location.address.addressLine2 ? ', ' + location.address.addressLine2 : ''
              }${location.address.addressLine3 ? ', ' + location.address.addressLine3 : ''}",
            "addressLocality":"${location.address.city ?? ''}",
            "addressRegion":"${location.address.state ?? ''}",
            "postalCode":"${location.address.zipCode ?? ''}",
            "telephone":"${location.phoneNumber?.[0] ?? ''}" 
        },`
            : ''
        }
        "geo": {
          "@type":"GeoCoordinates",
          "latitude":["${location.address?.latitude ?? ''}"],
          "longitude":["${location.address?.longitude ?? ''}"]
        },
        "openingHours": ${this.getOpeningHours(location.locationHours)},
        "medicalSpecialty":{"@type":"medicalSpecialty","name":[${specialities ? specialities : '""'}]}
    }`;
  }

  getOpeningHours(locationHours: LocationHour[]): string {
    const hours = locationHours.filter((x: any) => x.officeHourType === 'Office Hours');
    const officeHours = hours.map((x) => Object.assign({}, x));
    this.weekdays.forEach((day) => {
      if (!officeHours.some((hourItem) => hourItem.day.toLowerCase() === day.toLowerCase())) {
        officeHours.push({
          id: 0,
          day,
          openTime: '',
          closeTime: '',
          is24Hours: null,
          officeHourType: null
        });
      }
    });

    const sortedOfficeHours = officeHours.sort((a, b) => {
      const indexA = this.weekdays.findIndex((day) => a.day.toLowerCase() === day.toLocaleLowerCase());
      const indexB = this.weekdays.findIndex((day) => b.day.toLowerCase() === day.toLocaleLowerCase());
      return indexA - indexB;
    });

    let openHours = '';
    sortedOfficeHours.forEach((x) => {
      if (x.day && x.day.length > 2) {
        openHours += openHours.length > 0 ? ',' : '';
        openHours += `"${x.day.slice(0, 2)}`;
        if (x.is24Hours) {
          openHours += ` 00:00-23:59"`;
        } else if (x.openTime == '') {
          openHours += ' Closed"';
        } else {
          openHours += ` ${x.openTime?.slice(0, 5)}-${x.closeTime?.slice(0, 5)}"`;
        }
      }
    });

    openHours = openHours.length > 0 ? `[${openHours.trim()}]` : '';
    return openHours.trimEnd();
  }
}
